import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { US_VIEWBOX, US_PATHS } from "@/lib/usGeo";

const FILL = {
  high: "#2563EB",
  medium: "rgba(37,99,235,0.55)",
  low: "#1E293B",
  unknown: "#141d2e",
};

const LEVEL_LABEL = {
  high: "High activity",
  medium: "Moderate activity",
  low: "Lower / limited data",
  unknown: "Limited public data",
};

/**
 * Accurate geographic U.S. map (AK & HI positioned as insets).
 * Every state shape is selectable (pointer + keyboard). Hover/focus/selected
 * styling is handled in index.css (.us-geo) to avoid re-rendering on mousemove.
 */
export default function USGeoMap({
  states = [],
  selectedAbbr = null,
  activeAbbrs = null,     // Set of abbrs matching the current filter/search; others are dimmed
  onSelect,
  onHover,
  showTooltip = true,
  className = "",
}) {
  const navigate = useNavigate();
  const wrapRef = useRef(null);
  const tipRef = useRef(null);
  const byAbbr = {};
  states.forEach((s) => (byAbbr[s.abbr] = s));

  const select = (abbr) => {
    if (!abbr) return;
    if (onSelect) onSelect(abbr);
    else navigate(`/states/${abbr}`);
  };

  const moveTip = (e) => {
    const abbr = e.target?.dataset?.abbr;
    const tip = tipRef.current;
    if (!tip || !wrapRef.current) return;
    if (!abbr) { tip.style.opacity = "0"; return; }
    const st = byAbbr[abbr];
    const name = st?.name || abbr;
    const lvl = LEVEL_LABEL[st?.capacity_level || "unknown"];
    tip.innerHTML = `<span class="font-display font-semibold text-white">${name}</span><span class="block font-mono text-[10px] text-[#84CC16] uppercase tracking-wider">${lvl}</span>`;
    const rect = wrapRef.current.getBoundingClientRect();
    let x = e.clientX - rect.left + 14;
    let y = e.clientY - rect.top + 14;
    if (x > rect.width - 150) x -= 164;
    tip.style.opacity = "1";
    tip.style.transform = `translate(${x}px, ${y}px)`;
  };

  const handleOver = (e) => {
    const abbr = e.target?.dataset?.abbr;
    if (abbr && onHover) onHover(byAbbr[abbr] || null);
  };
  const handleOut = (e) => {
    if (tipRef.current) tipRef.current.style.opacity = "0";
    if (onHover && !e.relatedTarget?.dataset?.abbr) onHover(null);
  };

  return (
    <div
      ref={wrapRef}
      className={`relative w-full ${className}`}
      onMouseMove={showTooltip ? moveTip : undefined}
      onMouseOver={handleOver}
      onMouseOut={handleOut}
      data-testid="us-geo-map"
    >
      <svg viewBox={US_VIEWBOX} className="us-geo w-full h-auto" role="group" aria-label="Interactive map of the United States">
        {Object.entries(US_PATHS).map(([abbr, { d, name }]) => {
          const st = byAbbr[abbr];
          const level = st?.capacity_level || "unknown";
          const dim = activeAbbrs && !activeAbbrs.has(abbr);
          const selected = selectedAbbr === abbr;
          return (
            <path
              key={abbr}
              d={d}
              data-abbr={abbr}
              data-testid={`map-state-${abbr}`}
              tabIndex={0}
              role="button"
              aria-label={`${name}${st ? `, ${LEVEL_LABEL[level]}` : ""}`}
              className={selected ? "selected" : ""}
              style={{ fill: FILL[level] || FILL.unknown, opacity: dim ? 0.22 : 1 }}
              onClick={() => select(abbr)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); select(abbr); } }}
            />
          );
        })}
      </svg>

      {/* AK / HI inset labels */}
      <span className="absolute left-[3%] bottom-[14%] font-mono text-[10px] text-slate-500 pointer-events-none">AK</span>
      <span className="absolute left-[24%] bottom-[4%] font-mono text-[10px] text-slate-500 pointer-events-none">HI</span>

      {showTooltip && (
        <div ref={tipRef}
          className="pointer-events-none absolute top-0 left-0 z-20 px-3 py-2 rounded-lg bg-[#0B0F17] border border-white/15 shadow-xl whitespace-nowrap"
          style={{ opacity: 0, transition: "opacity .12s ease", willChange: "transform" }} />
      )}
    </div>
  );
}
